// TableRowDetailsModal.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./TableRowDetailsModal.css";

import { getValue } from "./utils";
import TableRowPdfButton from "./TableRowPdfButton";
import DynamicButton from "../DynamicButton/DynamicButton";
import ConfirmDialog from "../ConfirmDialog/ConfirmDialog";

/* =========================
   Constants
========================= */

const OFFICE_EXTENSIONS = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"];
const DOCUMENT_EXTENSIONS = ["pdf", ...OFFICE_EXTENSIONS];



/* =========================
   Generic Helpers
========================= */

function isHttpUrl(value = "") {
  try {
    const url = new URL(String(value));
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function isImageUrl(url = "") {
  const cleanUrl = String(url || "").split("?")[0];
  return /\.(png|jpe?g|webp|gif|svg)$/i.test(cleanUrl);
}

function getFileName(url = "") {
  try {
    const cleanUrl = decodeURI(String(url));
    return cleanUrl.split("?")[0].split("#")[0].split("/").pop() || "file";
  } catch {
    return String(url).split("?")[0].split("#")[0].split("/").pop() || "file";
  }
}

function fileExt(url = "") {
  const fileName = getFileName(url);
  const match = fileName.match(/\.([a-z0-9]+)$/i);
  return match ? match[1].toLowerCase() : "";
}

function isPreviewableImage(url = "") {
  return isImageUrl(url);
}

function isEmpty(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value.trim() === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;

  if (typeof value === "object" && !Array.isArray(value)) {
    return Object.keys(value).length === 0;
  }

  return false;
}

function getBaseField(field = "") {
  return String(field || "").split(".")[0];
}

function getNestedValue(obj, path = "") {
  if (!obj || !path) return undefined;

  return String(path)
    .split(".")
    .reduce((acc, key) => {
      if (acc && typeof acc === "object" && key in acc) {
        return acc[key];
      }

      return undefined;
    }, obj);
}

function formatDetailValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function formatDateValue(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDisplayValue(value, column = {}) {
  if (value === null || value === undefined || value === "") return "—";

  if (column.type === "Date") {
    return formatDateValue(value);
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "object") {
    return objectLabel(value, column) || JSON.stringify(value);
  }

  return String(value);
}

function objectLabel(obj, column) {
  if (!obj || typeof obj !== "object") return "";

  const keyPreferences = [
    column?.apiTitle,
    "label",
    "name",
    "hazards",
    "tools",
    "machineTools",
    "equipments",
    "permitsType",
    "projectName",
    "siteLocation",
    "value",
    "title",
  ].filter(Boolean);

  for (const key of keyPreferences) {
    if (obj[key] !== undefined && obj[key] !== null) {
      return String(obj[key]);
    }
  }

  try {
    return JSON.stringify(obj);
  } catch {
    return String(obj);
  }
}

function dropdownLabelFromRow(row, column) {
  const { field, options, apiTitle } = column;
  const baseField = getBaseField(field);
  const selected = row?.[baseField];

  if (selected && typeof selected === "object") {
    const nestedPath = String(field || "")
      .split(".")
      .slice(1)
      .join(".");

    return (
      (apiTitle && selected[apiTitle]) ||
      selected.name ||
      selected.label ||
      selected.title ||
      selected.value ||
      getNestedValue(selected, nestedPath) ||
      ""
    );
  }

  if (options && Array.isArray(options) && options.includes(selected)) {
    return selected;
  }

  const fullValue = getValue(row, field);
  if (typeof fullValue === "string" && fullValue.trim()) return fullValue;

  return selected || "";
}

/* =========================
   Map Helpers
========================= */

function parseLatLng(geo = "") {
  const match = String(geo || "").match(
    /(-?\d+(\.\d+)?)[,\s)]+(-?\d+(\.\d+)?)/,
  );

  if (!match) return null;

  return {
    lat: match[1],
    lng: match[3],
  };
}

function googleMapsHref(geo = "") {
  const point = parseLatLng(geo);
  if (!point) return null;

  return `https://www.google.com/maps?q=${encodeURIComponent(
    point.lat,
  )},${encodeURIComponent(point.lng)}&z=15`;
}

function googleMapsEmbedSrc(geo = "") {
  const point = parseLatLng(geo);
  if (!point) return null;

  return `https://www.google.com/maps?q=${encodeURIComponent(
    point.lat,
  )},${encodeURIComponent(point.lng)}&z=15&output=embed`;
}

function isTrue(value) {
  return value === true || String(value).toLowerCase() === "true";
}

/* =========================
   File / Viewer Helpers
========================= */

function getMicrosoftOfficeViewerUrl(url) {
  return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(
    url,
  )}`;
}

function getGooglePdfViewerUrl(url) {
  return `https://docs.google.com/viewer?url=${encodeURIComponent(
    url,
  )}&embedded=true`;
}

function getFilePreviewUrl(url) {
  const ext = fileExt(url);

  if (OFFICE_EXTENSIONS.includes(ext)) {
    return getMicrosoftOfficeViewerUrl(url);
  }

  if (ext === "pdf") {
    return getGooglePdfViewerUrl(url);
  }

  return url;
}

function normalizeFileItems(rawValue) {
  const files = Array.isArray(rawValue) ? rawValue : rawValue ? [rawValue] : [];

  return files
    .map((file) => {
      if (!file) return null;

      if (typeof file === "string") {
        return {
          url: file,
          name: getFileName(file),
          ext: fileExt(file),
        };
      }

      const url =
        file.url ||
        file.fileUrl ||
        file.fileURL ||
        file.path ||
        file.location ||
        file.secure_url ||
        file.downloadUrl ||
        file.href ||
        file.name;

      if (!url) return null;

      return {
        url,
        name:
          file.originalname ||
          file.originalName ||
          file.filename ||
          file.fileName ||
          file.name ||
          getFileName(url),
        ext: fileExt(url),
      };
    })
    .filter(Boolean);
}

function getArrayItems(row, column, rawValue) {
  const baseField = getBaseField(column.field);
  const baseValue = row?.[baseField];

  if (Array.isArray(baseValue)) return baseValue;
  if (Array.isArray(rawValue)) return rawValue;

  if (baseValue && typeof baseValue === "object") return [baseValue];

  if (baseValue !== null && baseValue !== undefined && baseValue !== "") {
    return [baseValue];
  }

  if (rawValue !== null && rawValue !== undefined && rawValue !== "") {
    return [rawValue];
  }

  return [];
}

function getArrayChipLabel(item, column) {
  if (item === null || item === undefined || item === "") return "—";

  if (typeof item === "object") {
    const nestedPath = String(column.field || "")
      .split(".")
      .slice(1)
      .join(".");

    return (
      getNestedValue(item, nestedPath) ||
      objectLabel(item, column) ||
      item.name ||
      item.label ||
      item.title ||
      item.value ||
      "—"
    );
  }

  return String(item);
}

function normalizeComparable(value) {
  if (value === null || value === undefined) return "";

  if (typeof value === "object") {
    return String(value._id || value.id || value.userId || value.value || "")
      .trim()
      .toLowerCase();
  }

  return String(value).trim().toLowerCase();
}

function extractApiArray(payload) {
  const candidates = [
    payload?.data?.data,
    payload?.data?.users,
    payload?.data?.result,
    payload?.data?.results,
    payload?.data,
    payload?.users,
    payload?.result,
    payload?.results,
    payload,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  return [];
}

function getItemMatchingKeys(item, column = {}) {
  const nestedPath = String(column.field || "")
    .split(".")
    .slice(1)
    .join(".");

  if (item && typeof item === "object") {
    return [
      item._id,
      item.id,
      item.userId,
      item.value,
      item.name,
      item.emailId,
      item.email,
      column.apiTitle ? item[column.apiTitle] : "",
      nestedPath ? getNestedValue(item, nestedPath) : "",
    ]
      .map(normalizeComparable)
      .filter(Boolean);
  }

  return [normalizeComparable(item)].filter(Boolean);
}

function getRecordMatchingKeys(record, column = {}) {
  if (!record || typeof record !== "object") return [];

  return [
    record._id,
    record.id,
    record.userId,
    record.value,
    record.name,
    record.emailId,
    record.email,
    column.apiTitle ? record[column.apiTitle] : "",
  ]
    .map(normalizeComparable)
    .filter(Boolean);
}

function findApiRecordForItem(item, apiRows = [], column = {}) {
  const itemKeys = getItemMatchingKeys(item, column);

  if (!itemKeys.length || !Array.isArray(apiRows) || !apiRows.length) {
    return null;
  }

  return (
    apiRows.find((record) => {
      const recordKeys = getRecordMatchingKeys(record, column);
      return recordKeys.some((recordKey) => itemKeys.includes(recordKey));
    }) || null
  );
}

function buildDetailSource(item, apiRows = [], column = {}) {
  const apiRecord = findApiRecordForItem(item, apiRows, column);

  if (apiRecord && item && typeof item === "object") {
    return {
      ...apiRecord,
      ...item,
    };
  }

  if (apiRecord) return apiRecord;

  if (item && typeof item === "object") return item;

  return null;
}

function ArrayField({ row, label, column, raw }) {
  const items = getArrayItems(row, column, raw);
  const showDetails = isTrue(column.details);
  const detailFields = Array.isArray(column.BoxShow) ? column.BoxShow : [];

  const [apiRows, setApiRows] = useState([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    if (!showDetails || !column.api || !detailFields.length || !items.length) {
      return;
    }

    const controller = new AbortController();

    const fetchDetailsData = async () => {
      try {
        setApiLoading(true);
        setApiError("");

        const token =
          localStorage.getItem("token") ||
          localStorage.getItem("accessToken") ||
          localStorage.getItem("authToken");

        const response = await fetch(column.api, {
          method: "GET",
          signal: controller.signal,
          headers: {
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch ${label} details`);
        }

        const payload = await response.json();
        setApiRows(extractApiArray(payload));
      } catch (error) {
        if (error.name !== "AbortError") {
          setApiError(error.message || "Unable to load details");
        }
      } finally {
        setApiLoading(false);
      }
    };

    fetchDetailsData();

    return () => controller.abort();
  }, [showDetails, column.api, detailFields.length, items.length, label]);

  if (!items.length) {
    return <EmptyField label={label} column={column} />;
  }

  const selectedSource = selectedItem
    ? buildDetailSource(selectedItem, apiRows, column)
    : null;

  return (
    <>
      <FieldShell label={label} column={column}>
        <div className="safety-row-details__chipset">
          {items.map((item, itemIndex) => {
            const detailSource = buildDetailSource(item, apiRows, column);
            const resolvedLabel =
              getArrayChipLabel(detailSource || item, column) ||
              objectLabel(detailSource, column) ||
              "—";

            const canShowDetails = showDetails && detailFields.length;

            return (
              <div
                key={
                  item?._id ||
                  item?.id ||
                  item?.userId ||
                  `${resolvedLabel}-${itemIndex}`
                }
                className={`safety-row-details__chip-group ${
                  canShowDetails
                    ? "safety-row-details__chip-group--details"
                    : ""
                }`}
              >
                {canShowDetails ? (
                  <button
                    type="button"
                    className="safety-row-details__chip safety-row-details__chip--button"
                    onClick={() => setSelectedItem(item)}
                    title={`View ${resolvedLabel} details`}
                  >
                    {resolvedLabel}
                  </button>
                ) : (
                  <span className="safety-row-details__chip">
                    {resolvedLabel}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </FieldShell>

      {selectedItem ? (
        <DetailsPopup
          title={`${label} Details`}
          source={selectedSource}
          fields={detailFields}
          loading={apiLoading}
          error={apiError}
          fallbackText="Details not found for this selected user."
          onClose={() => setSelectedItem(null)}
        />
      ) : null}
    </>
  );
}

/* =========================
   Reusable Render Pieces
========================= */

function getFieldClassName(column, extraClass = "") {
  const classes = ["safety-row-details__field"];

  if (column?.full) classes.push("safety-row-details__field--full");
  if (extraClass) classes.push(extraClass);

  return classes.join(" ");
}

function FieldShell({ label, column, children, details, className = "" }) {
  return (
    <div className={getFieldClassName(column, className)}>
      <div className="safety-row-details__label">{label}</div>

      <div className="safety-row-details__value">{children}</div>

      {details}
    </div>
  );
}

function EmptyField({ label, column }) {
  return (
    <FieldShell label={label} column={column}>
      <span className="safety-row-details__dash">—</span>
    </FieldShell>
  );
}

function DetailsBox({ source, fields = [] }) {
  if (!source || !fields?.length) return null;

  return (
    <div className="safety-row-details__details-card">
      <ul className="safety-row-details__details-list">
        {fields.map((fieldKey) => {
          const value = getNestedValue(source, fieldKey);

          return (
            <li key={fieldKey} className="safety-row-details__details-item">
              <div className="safety-row-details__details-key">{fieldKey}</div>

              <div className="safety-row-details__details-value">
                {formatDetailValue(value)}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ArrayItemDetailsBox({ item, fields = [] }) {
  if (!item || typeof item !== "object" || !fields?.length) return null;

  return (
    <div className="safety-row-details__details-card">
      <ul className="safety-row-details__details-list">
        {fields.map((fieldKey) => {
          const value = getNestedValue(item, fieldKey);

          return (
            <li key={fieldKey} className="safety-row-details__details-item">
              <div className="safety-row-details__details-key">{fieldKey}</div>

              <div className="safety-row-details__details-value">
                {formatDetailValue(value)}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* =========================
   Field Type Renderers
========================= */

function DetailsPopup({
  title = "Details",
  source,
  fields = [],
  loading = false,
  error = "",
  fallbackText = "No details found.",
  onClose,
}) {
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="safety-details-popup"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={onClose}
    >
      <div
        className="safety-details-popup__box"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="safety-details-popup__header">
          <h3>{title}</h3>

          <button
            type="button"
            className="safety-details-popup__close"
            onClick={onClose}
            aria-label="Close details"
          >
            ×
          </button>
        </div>

        <div className="safety-details-popup__body">
          {loading ? (
            <div className="safety-details-popup__state">
              Loading details...
            </div>
          ) : error ? (
            <div className="safety-details-popup__state safety-details-popup__state--error">
              {error}
            </div>
          ) : !source ? (
            <div className="safety-details-popup__state">{fallbackText}</div>
          ) : (
            <div className="safety-details-popup__grid">
              {fields.map((fieldKey) => {
                const value = getNestedValue(source, fieldKey);

                return (
                  <div key={fieldKey} className="safety-details-popup__item">
                    <div className="safety-details-popup__key">{fieldKey}</div>

                    <div className="safety-details-popup__value">
                      {formatDetailValue(value)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ImageLinkField({ label, column, raw }) {
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewError, setPreviewError] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [thumbErrors, setThumbErrors] = useState({});

  const urls = Array.isArray(raw) ? raw : raw ? [raw] : [];
  const validUrls = urls.filter((url) => isHttpUrl(url));

  const closePreview = () => {
    setPreviewUrl("");
    setPreviewError(false);
    setPreviewLoading(false);
  };

  const openPreview = (url) => {
    setPreviewUrl(url);
    setPreviewError(false);
    setPreviewLoading(isImageUrl(url));
  };

  const markThumbError = (url) => {
    setThumbErrors((prev) => ({
      ...prev,
      [url]: true,
    }));
  };

  useEffect(() => {
    if (!previewUrl) return;

    const handleEsc = (event) => {
      if (event.key === "Escape") closePreview();
    };

    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [previewUrl]);

  if (!validUrls.length) {
    return <EmptyField label={label} column={column} />;
  }

  return (
    <>
      <FieldShell
        label={label}
        column={column}
        className="safety-row-details__field--images"
      >
        <div className="safety-image-fields">
          <div className="safety-image-fields__meta">
            <span>
              {validUrls.length} photo{validUrls.length === 1 ? "" : "s"}{" "}
              present
            </span>
          </div>

          <div className="safety-image-fields__grid">
            {validUrls.map((url, imageIndex) => {
              const fileName =
                getFileName(url) || `Attachment ${imageIndex + 1}`;

              const isImage = isImageUrl(url);
              const hasThumbError = Boolean(thumbErrors[url]);

              return (
                <button
                  key={`${url}-${imageIndex}`}
                  type="button"
                  className={`safety-image-card ${
                    isImage
                      ? "safety-image-card--image"
                      : "safety-image-card--file"
                  }`}
                  onClick={() => openPreview(url)}
                  title={`Open ${fileName}`}
                >
                  <div
                    className={`safety-image-card__preview ${
                      isImage && hasThumbError
                        ? "safety-image-card__preview--photo-present"
                        : ""
                    }`}
                  >
                    {isImage && !hasThumbError ? (
                      <img
                        src={url}
                        alt={fileName}
                        loading="lazy"
                        onError={() => markThumbError(url)}
                      />
                    ) : isImage ? (
                      <div className="safety-image-card__photo-present">
                        <span>Photo Present</span>
                        <small>Click to view</small>
                      </div>
                    ) : (
                      <span className="safety-image-card__file-icon">
                        {fileExt(url)?.toUpperCase() || "FILE"}
                      </span>
                    )}
                  </div>

                  <div className="safety-image-card__info">
                    <strong>{fileName}</strong>
                    <span>
                      {isImage ? "Full panel clickable" : "Click to open file"}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </FieldShell>

      {previewUrl ? (
        <div
          className="safety-preview-modal"
          role="dialog"
          aria-modal="true"
          aria-label={`${label} preview`}
          onMouseDown={closePreview}
        >
          <div
            className="safety-preview-modal__box"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="safety-preview-modal__header">
              <div>
                <div className="safety-preview-modal__eyebrow">{label}</div>
                <div className="safety-preview-modal__title">
                  {getFileName(previewUrl) || "Attachment Preview"}
                </div>
              </div>

              <div className="safety-preview-modal__actions">
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="safety-preview-modal__action-btn"
                >
                  Open
                </a>

                <a
                  href={previewUrl}
                  download
                  className="safety-preview-modal__action-btn"
                >
                  Download
                </a>

                <button
                  type="button"
                  className="safety-preview-modal__close"
                  onClick={closePreview}
                  aria-label="Close preview"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="safety-preview-modal__body">
              {isImageUrl(previewUrl) && !previewError ? (
                <>
                  {previewLoading ? (
                    <div className="safety-preview-modal__loader">
                      Loading photo preview...
                    </div>
                  ) : null}

                  <img
                    className="safety-preview-modal__image"
                    src={previewUrl}
                    alt={label}
                    onLoad={() => setPreviewLoading(false)}
                    onError={() => {
                      setPreviewLoading(false);
                      setPreviewError(true);
                    }}
                  />
                </>
              ) : (
                <div className="safety-preview-modal__fallback">
                  <div className="safety-preview-modal__file-badge">
                    {isImageUrl(previewUrl)
                      ? "PHOTO"
                      : fileExt(previewUrl)?.toUpperCase() || "FILE"}
                  </div>

                  <h4>
                    {isImageUrl(previewUrl)
                      ? "Photo is present"
                      : "Preview not available"}
                  </h4>

                  <p>
                    The attachment exists, but the browser could not preview it
                    directly here. You can still open or download it.
                  </p>

                  <div className="safety-preview-modal__fallback-actions">
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="safety-preview-modal__open-link"
                    >
                      Open file
                    </a>

                    <a
                      href={previewUrl}
                      download
                      className="safety-preview-modal__download-link"
                    >
                      Download file
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function SignatureField({ label, column, raw }) {
  if (!isHttpUrl(raw)) {
    return <EmptyField label={label} column={column} />;
  }

  return (
    <FieldShell
      label={label}
      column={column}
      className="safety-row-details__field--images"
    >
      <div className="safety-row-details__signature-preview">
        <img
          className="safety-row-details__signature"
          src={raw}
          alt={`${label} signature`}
          onClick={() => window.open(raw, "_blank", "noopener,noreferrer")}
          onError={(event) => {
            event.currentTarget.replaceWith(
              Object.assign(document.createElement("span"), {
                className: "safety-row-details__dash",
                textContent: "—",
              }),
            );
          }}
        />

        <a
          href={raw}
          target="_blank"
          rel="noopener noreferrer"
          className="safety-row-details__link"
        >
          View Full Signature
        </a>
      </div>
    </FieldShell>
  );
}

function DropdownField({ row, label, column }) {
  const labelText = dropdownLabelFromRow(row, column);
  const baseField = getBaseField(column.field);
  const selectedOption = row?.[baseField];

  return (
    <FieldShell
      label={label}
      column={column}
      details={
        column.details === "True" && selectedOption ? (
          <DetailsBox source={selectedOption} fields={column.BoxShow} />
        ) : null
      }
    >
      {labelText ? (
        <span className="safety-row-details__chip">{labelText}</span>
      ) : (
        <span className="safety-row-details__dash">—</span>
      )}
    </FieldShell>
  );
}

// function ArrayField({ row, label, column, raw }) {
//   const items = getArrayItems(row, column, raw);

//   if (!items.length) {
//     return <EmptyField label={label} column={column} />;
//   }

//   return (
//     <FieldShell label={label} column={column}>
//       <div className="safety-row-details__chipset">
//         {items.map((item, itemIndex) => {
//           const chipLabel =
//             typeof item === "object" ? objectLabel(item, column) : String(item);

//           return (
//             <div
//               key={item?._id || `${chipLabel}-${itemIndex}`}
//               className="safety-row-details__chip-group"
//             >
//               <span className="safety-row-details__chip">{chipLabel}</span>

//               {column.details === "True" && typeof item === "object" && (
//                 <ArrayItemDetailsBox item={item} fields={column.BoxShow} />
//               )}
//             </div>
//           );
//         })}
//       </div>
//     </FieldShell>
//   );
// }

function GeolocationField({ label, column, raw }) {
  const embedSrc = googleMapsEmbedSrc(raw);
  const href = googleMapsHref(raw);

  return (
    <FieldShell
      label={label}
      column={column}
      className="safety-row-details__field--map"
    >
      {embedSrc ? (
        <>
          <div className="safety-row-details__map-embed">
            <iframe
              title="Map"
              src={embedSrc}
              width="100%"
              height="100%"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>

          {href && (
            <a
              className="safety-row-details__link"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open in Google Maps
            </a>
          )}
        </>
      ) : (
        <span className="safety-row-details__dash">—</span>
      )}
    </FieldShell>
  );
}

function BooleanField({ label, column, raw }) {
  return (
    <FieldShell label={label} column={column}>
      <span
        className={`safety-row-details__chip ${
          raw
            ? "safety-row-details__chip--success"
            : "safety-row-details__chip--muted"
        }`}
      >
        {raw ? "Yes" : "No"}
      </span>
    </FieldShell>
  );
}

function FileField({ row, label, column }) {
  const baseField = getBaseField(column.field);
  const rawValue = row?.[baseField];
  const files = normalizeFileItems(rawValue);

  if (!files.length) {
    return <EmptyField label={label} column={column} />;
  }

  return (
    <FieldShell label={label} column={column}>
      <div className="safety-row-details__file-list">
        {files.map((file, fileIndex) => {
          const { url, name, ext } = file;

          if (!isHttpUrl(url)) {
            return (
              <span
                key={`${name}-${fileIndex}`}
                className="safety-row-details__dash"
              >
                —
              </span>
            );
          }

          if (isPreviewableImage(url)) {
            return (
              <img
                key={`${url}-${fileIndex}`}
                className="safety-row-details__image"
                src={url}
                alt={name}
                title={name}
                onClick={() =>
                  window.open(url, "_blank", "noopener,noreferrer")
                }
                onError={(event) => {
                  event.currentTarget.replaceWith(
                    Object.assign(document.createElement("a"), {
                      href: url,
                      target: "_blank",
                      rel: "noopener noreferrer",
                      className: "safety-row-details__link",
                      textContent: name,
                    }),
                  );
                }}
              />
            );
          }

          if (DOCUMENT_EXTENSIONS.includes(ext)) {
            const viewerUrl = getFilePreviewUrl(url);

            return (
              <a
                key={`${url}-${fileIndex}`}
                href={viewerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="safety-row-details__file-link"
                title={name}
              >
                <span className="safety-row-details__file-badge">{ext}</span>

                <span className="safety-row-details__file-name">{name}</span>
              </a>
            );
          }

          return (
            <a
              key={`${url}-${fileIndex}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="safety-row-details__link"
              title={name}
            >
              {name}
            </a>
          );
        })}
      </div>
    </FieldShell>
  );
}

function getSubtableItems(row, column) {
  if (!row || !column) return [];

  const rawValue = getValue(row, column.field);

  // Case 1: exact field is already an array
  if (Array.isArray(rawValue)) {
    return rawValue;
  }

  // Case 2: field is nested like SafetyChecks.CheckPoints
  // then use base field SafetyChecks
  if (typeof column.field === "string" && column.field.includes(".")) {
    const baseField = getBaseField(column.field);
    const baseValue = row?.[baseField];

    if (Array.isArray(baseValue)) {
      return baseValue;
    }
  }

  // Case 3: fallback for safety checks old structure
  if (Array.isArray(row?.SafetyChecks)) {
    return row.SafetyChecks;
  }

  // Case 4: fallback lowercase also
  if (Array.isArray(row?.safetyChecks)) {
    return row.safetyChecks;
  }

  return [];
}

function PermitChecklistField({ row, label, column }) {
  const items = getSubtableItems(row, column);

  const checkLabel =
    column.subFields?.find((subField) => subField.field === "CheckPoints")
      ?.label || "Check Points";

  const responseLabel =
    column.subFields?.find((subField) => subField.field === "response")
      ?.label || "Response";

  return (
    <FieldShell
      label={label}
      column={column}
      className="safety-row-details__field--subtable"
    >
      {items.length ? (
        <div className="safety-row-details__subtable-scroll">
          <table className="safety-row-details__subtable">
            <thead>
              <tr>
                <th>{checkLabel}</th>
                <th>{responseLabel}</th>
              </tr>
            </thead>

            <tbody>
              {items.map((item, itemIndex) => (
                <tr key={item._id ?? itemIndex}>
                  <td>{item?.CheckPoints || "—"}</td>
                  <td>{item?.response ?? "N/A"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <span className="safety-row-details__dash">—</span>
      )}
    </FieldShell>
  );
}

function GenericSubtableField({ row, label, column }) {
  const items = getSubtableItems(row, column);
  const subFields = Array.isArray(column.subFields) ? column.subFields : [];

  return (
    <FieldShell
      label={label}
      column={column}
      className="safety-row-details__field--subtable"
    >
      {items.length && subFields.length ? (
        <div className="safety-row-details__subtable-scroll">
          <table className="safety-row-details__subtable">
            <thead>
              <tr>
                {subFields.map((subField) => (
                  <th key={subField.field}>
                    {subField.label || subField.field}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {items.map((item, itemIndex) => (
                <tr key={item._id ?? itemIndex}>
                  {subFields.map((subField) => {
                    const value = getValue(item, subField.field);

                    if (
                      subField.type === "imageLink" &&
                      typeof value === "string"
                    ) {
                      return (
                        <td key={subField.field}>
                          {value ? (
                            <img
                              src={value}
                              alt={subField.label}
                              className="safety-row-details__subtable-image"
                              onClick={() =>
                                window.open(
                                  value,
                                  "_blank",
                                  "noopener,noreferrer",
                                )
                              }
                              onError={(event) => {
                                event.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            "—"
                          )}
                        </td>
                      );
                    }

                    if (subField.type === "dropdown") {
                      return (
                        <td key={subField.field}>
                          {value ? (
                            <span className="safety-row-details__chip">
                              {String(value)}
                            </span>
                          ) : (
                            subField.prefilled || "—"
                          )}
                        </td>
                      );
                    }

                    if (value && typeof value === "object") {
                      return (
                        <td key={subField.field}>
                          {objectLabel(value, subField) || "—"}
                        </td>
                      );
                    }

                    return (
                      <td key={subField.field}>
                        {value ? String(value) : "—"}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <span className="safety-row-details__dash">—</span>
      )}
    </FieldShell>
  );
}

function PrimitiveField({ label, column, raw }) {
  return (
    <FieldShell label={label} column={column}>
      {String(formatDisplayValue(raw, column)).trim() === "" ? (
        <span className="safety-row-details__dash">—</span>
      ) : (
        formatDisplayValue(raw, column)
      )}
    </FieldShell>
  );
}

/* =========================
   Main Component
========================= */

export default function TableRowDetailsModal({
  open,
  row,
  columns = [],
  onClose,
  onEdit,
  onDelete,
  title = "Details",

  onPrev,
  onNext,
  hasPrev = true,
  hasNext = true,
  loop = false,
}) {
  const [showConfirm, setShowConfirm] = useState(false);

  const safeTitle = useMemo(() => {
    return String(title || "Details");
  }, [title]);

  const pdfFilename = useMemo(() => {
    const cleanTitle = safeTitle.replace(/\s+/g, "_").toLowerCase();
    return `${cleanTitle}_${row?._id || row?.reportId || "record"}.pdf`;
  }, [safeTitle, row]);

  const orderedColumns = useMemo(() => {
  const regularColumns = [];
  const imageColumns = [];

  columns.forEach((column, originalIndex) => {
    const entry = { column, originalIndex };
    const type = String(column?.type || "").toLowerCase();

    if (type === "imagelink") {
      imageColumns.push(entry);
    } else {
      regularColumns.push(entry);
    }
  });

  return [...regularColumns, ...imageColumns];
}, [columns]);

  const handleRequestDelete = () => {
    setShowConfirm(true);
  };

  const handleConfirmDelete = () => {
    setShowConfirm(false);
    onDelete?.();
  };

  const handleCancelDelete = () => {
    setShowConfirm(false);
  };

  const handleKeyDown = useCallback(
    (event) => {
      if (!open) return;

      if (event.key === "Escape") {
        onClose?.();
      }

      if (event.key === "ArrowLeft") {
        if (loop || hasPrev) onPrev?.();
      }

      if (event.key === "ArrowRight") {
        if (loop || hasNext) onNext?.();
      }
    },
    [open, onClose, onPrev, onNext, hasPrev, hasNext, loop],
  );

  useEffect(() => {
    if (!open) return;

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, handleKeyDown]);

  if (!open || !row) return null;

const renderField = (column, index) => {
  const label = column.label || column.field || `Field ${index + 1}`;
  const raw = getValue(row, column.field);

  if (column.type === "imageLink") {
    return (
      <ImageLinkField
        key={`${column.field}-${index}`}
        label={label}
        column={column}
        raw={raw}
      />
    );
  }

  if (column.type === "signature") {
    return (
      <SignatureField
        key={`${column.field}-${index}`}
        label={label}
        column={column}
        raw={raw}
      />
    );
  }

  if (column.type === "dropdown") {
    return (
      <DropdownField
        key={`${column.field}-${index}`}
        row={row}
        label={label}
        column={column}
      />
    );
  }

  if (column.type === "file") {
    return (
      <FileField
        key={`${column.field}-${index}`}
        row={row}
        label={label}
        column={column}
      />
    );
  }

if (
  (column.type === "checkbox" && isTrue(column.array)) ||
  Array.isArray(raw) ||
  Array.isArray(row?.[getBaseField(column.field)])
) {
  return (
    <ArrayField
      key={`${column.field}-${index}`}
      row={row}
      label={label}
      column={column}
      raw={raw}
    />
  );
}

  if (column.type === "geolocation") {
    return (
      <GeolocationField
        key={`${column.field}-${index}`}
        label={label}
        column={column}
        raw={raw}
      />
    );
  }

  if (typeof raw === "boolean") {
    return (
      <BooleanField
        key={`${column.field}-${index}`}
        label={label}
        column={column}
        raw={raw}
      />
    );
  }

  if (column.type === "permitchecklists") {
    return (
      <PermitChecklistField
        key={`${column.field}-${index}`}
        row={row}
        label={label}
        column={column}
      />
    );
  }

  if (column.type === "safetychecks" || column.type === "subString") {
    return (
      <GenericSubtableField
        key={`${column.field}-${index}`}
        row={row}
        label={label}
        column={column}
      />
    );
  }

  if (isEmpty(raw)) {
    return (
      <EmptyField
        key={`${column.field}-${index}`}
        label={label}
        column={column}
      />
    );
  }

  return (
    <PrimitiveField
      key={`${column.field}-${index}`}
      label={label}
      column={column}
      raw={raw}
    />
  );
};

  return (
    <div
      className="safety-row-details"
      role="dialog"
      aria-modal="true"
      aria-label={safeTitle}
    >
      <div className="safety-row-details__dialog">
        <div className="safety-row-details__header">
          <h3 className="safety-row-details__title">{safeTitle}</h3>

          <button
            type="button"
            className="safety-row-details__close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="safety-row-details__content">
          <div className="safety-row-details__grid">
            {orderedColumns.map(({ column, originalIndex }) =>
  renderField(column, originalIndex),
)}
          </div>
        </div>

        <div className="safety-row-details__footer">
          {onDelete && (
            <DynamicButton
              text="Delete"
              icon="fa-trash"
              size="md"
              variant="danger"
              bgColor="#ffffff"
              textColor="#b91c1c"
              outlineColor="#fecaca"
              hoverBgColor="#b91c1c"
              hoverTextColor="#ffffff"
              title="Delete this record"
              ariaLabel="Delete this record"
              onClick={handleRequestDelete}
            />
          )}

          <div className="safety-row-details__spacer" />

          <TableRowPdfButton
            row={row}
            columns={columns}
            title={safeTitle}
            filename={pdfFilename}
            directFileDownload={true}
            fileDownloadField={null}
            className="safety-row-details__pdf-button"
            size="md"
            variant="info"
            icon="fa-download"
          >
            Download
          </TableRowPdfButton>

          {onEdit && (
            <DynamicButton
              text="Edit"
              icon="fa-edit"
              size="md"
              variant="outline"
              bgColor="#ffffff"
              textColor="#374151"
              outlineColor="#d1d5db"
              hoverBgColor="#111827"
              hoverTextColor="#ffffff"
              title="Edit this record"
              ariaLabel="Edit this record"
              onClick={onEdit}
            />
          )}

          {(onPrev || onNext) && (
            <div className="safety-row-details__nav-group">
              <DynamicButton
                icon="fa-arrow-left"
                size="sm"
                variant="outline"
                bgColor="#ffffff"
                textColor="#374151"
                outlineColor="#d1d5db"
                hoverBgColor="#111827"
                hoverTextColor="#ffffff"
                title="Previous record"
                ariaLabel="Previous record"
                onClick={() => (loop || hasPrev) && onPrev?.()}
                disabled={!loop && !hasPrev}
              />

              <DynamicButton
                icon="fa-arrow-right"
                size="sm"
                variant="outline"
                bgColor="#ffffff"
                textColor="#374151"
                outlineColor="#d1d5db"
                hoverBgColor="#111827"
                hoverTextColor="#ffffff"
                title="Next record"
                ariaLabel="Next record"
                onClick={() => (loop || hasNext) && onNext?.()}
                disabled={!loop && !hasNext}
              />
            </div>
          )}

          <ConfirmDialog
            open={showConfirm}
            title="Delete this record?"
            message="Are you sure you want to delete this record? This action cannot be undone."
            confirmLabel="Delete"
            cancelLabel="Cancel"
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
            variant="danger"
          />
        </div>
      </div>
    </div>
  );
}
